import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bug, Droplets, Factory, Recycle, TreePine,
  CheckCircle2, ArrowRight, Phone
} from 'lucide-react';
import PageSEO from '../components/PageSEO';

const servicesSEO = {
  title: 'Our Services | Fumigation, Pest Control, Sanitary Disposal & Waste Management – Lidav Kenya',
  description: 'Explore Lidav Enterprises\' full range of NEMA-licensed services: fumigation & pest control, sanitary disposal, hazardous industrial waste management, general waste management and landscaping across Kenya.',
  canonical: '/services',
  schema: {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': 'https://lidaventerprises.com/services',
        name: 'Our Services – Lidav Enterprises Limited',
        url: 'https://lidaventerprises.com/services',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://lidaventerprises.com' },
            { '@type': 'ListItem', position: 2, name: 'Our Services', item: 'https://lidaventerprises.com/services' },
          ],
        },
      },
      {
        '@type': 'Service',
        name: 'Fumigation & Pest Control',
        provider: { '@type': 'Organization', name: 'Lidav Enterprises Limited' },
        areaServed: 'Kenya',
        description: 'NEMA-licensed fumigation and pest control services for homes, offices and industrial facilities across Kenya. Eliminates cockroaches, rodents, bed bugs, termites and all common pests.',
        serviceType: 'Pest Control & Fumigation',
        url: 'https://lidaventerprises.com/services',
      },
      {
        '@type': 'Service',
        name: 'Sanitary Disposal Services',
        provider: { '@type': 'Organization', name: 'Lidav Enterprises Limited' },
        areaServed: 'Kenya',
        description: 'Professional sanitary waste disposal and feminine hygiene waste management for offices, schools, hospitals and commercial facilities in Kenya.',
        serviceType: 'Sanitary Waste Disposal',
        url: 'https://lidaventerprises.com/services',
      },
      {
        '@type': 'Service',
        name: 'Hazards & Industrial Waste Management',
        provider: { '@type': 'Organization', name: 'Lidav Enterprises Limited' },
        areaServed: 'Kenya',
        description: 'Licensed handling, transportation and disposal of chemical waste, clinical waste, e-waste and hazardous materials in compliance with NEMA and KEBS regulations.',
        serviceType: 'Hazardous Waste Management',
        url: 'https://lidaventerprises.com/services',
      },
      {
        '@type': 'Service',
        name: 'General Waste Management',
        provider: { '@type': 'Organization', name: 'Lidav Enterprises Limited' },
        areaServed: 'Kenya',
        description: 'Comprehensive general waste collection, sorting, recycling and responsible disposal for commercial, industrial and residential clients across Kenya.',
        serviceType: 'Waste Management',
        url: 'https://lidaventerprises.com/services',
      },
      {
        '@type': 'Service',
        name: 'Landscaping Services',
        provider: { '@type': 'Organization', name: 'Lidav Enterprises Limited' },
        areaServed: 'Kenya',
        description: 'Professional landscaping, garden maintenance, lawn care and outdoor space design for commercial and residential properties in Nairobi and Kenya.',
        serviceType: 'Landscaping',
        url: 'https://lidaventerprises.com/services',
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What fumigation and pest control services does Lidav Enterprises offer in Kenya?',
            acceptedAnswer: { '@type': 'Answer', text: 'Lidav Enterprises offers NEMA-licensed fumigation and pest control services including cockroach, rodent, bed bug, termite and general pest elimination for homes, offices, schools, hospitals and industrial facilities across Kenya. Emergency same-day response is available.' },
          },
          {
            '@type': 'Question',
            name: 'Is Lidav Enterprises NEMA licensed for waste management in Kenya?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. Lidav Enterprises Limited is fully licensed by NEMA (National Environment Management Authority) and complies with KEBS regulations for hazardous waste handling, transportation and disposal in Kenya.' },
          },
          {
            '@type': 'Question',
            name: 'Does Lidav handle hazardous and industrial waste in Nairobi?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. Lidav Enterprises is licensed to handle chemical waste, clinical waste, e-waste, asbestos and oil waste. We provide manifested waste transfer, chain-of-custody documentation and emergency hazmat spill response across Kenya.' },
          },
          {
            '@type': 'Question',
            name: 'What areas in Kenya does Lidav Enterprises serve?',
            acceptedAnswer: { '@type': 'Answer', text: 'Lidav Enterprises serves all of Nairobi and the Greater Nairobi area, plus Mombasa, Kisumu, Nakuru and Eldoret. We also handle national projects across Kenya for corporate and government clients.' },
          },
          {
            '@type': 'Question',
            name: 'How do I get a free quote from Lidav Enterprises?',
            acceptedAnswer: { '@type': 'Answer', text: 'Contact Lidav Enterprises at 0722 148 999 or info@lidaventerprises.com, or fill in the enquiry form on our Contact page. We offer free site assessments and respond within 1 business hour.' },
          },
          {
            '@type': 'Question',
            name: 'What sanitary disposal services does Lidav offer?',
            acceptedAnswer: { '@type': 'Answer', text: 'Lidav provides hygienic feminine sanitary waste disposal bins and scheduled collection services for offices, schools, hospitals, malls and hospitality facilities. Services include bin installation, regular servicing and compliant disposal of all sanitary waste.' },
          },
        ],
      },
    ],
  },
};

const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 36, scale: 0.98 },
  whileInView: { opacity: 1, y: 0,  scale: 1    },
  viewport:    { once: true, margin: '-80px'     },
  transition:  { duration: 0.6, ease: 'easeOut', delay },
});
const stagger     = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const staggerItem = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  show:   { opacity: 1, y: 0,  scale: 1, transition: { duration: 0.5 } },
};

const services = [
  {
    id: 'pest-control', icon: Bug,
    gradient: 'from-red-600 to-red-800', bg: 'bg-red-50', accent: 'text-brand-red',
    title: 'Fumigation & Pest Control Services', tagline: 'Complete elimination. Guaranteed protection.',
    image: '/images/2.jpg',
    desc: 'We provide comprehensive pest management solutions using industry-approved, eco-safe chemicals. Our certified technicians target all common and exotic pest infestations, delivering guaranteed results with minimal disruption to your operations.',
    features: ['Bedbugs — full thermal and chemical treatment','Cockroaches — gel baiting and spray treatment','Termites — soil treatment and baiting systems','Rodents — snap traps, glue boards and baiting','Mosquitoes — larviciding and fogging','Flies — UV light traps and residual treatment','Ants, fleas, ticks and other crawling insects','Pre-construction soil treatment'],
    benefits: ['NEMA-approved chemicals safe for humans and pets','30-day warranty on all treatments','Same-day emergency response available','Regular monitoring and follow-up visits'],
  },
  {
    id: 'sanitary', icon: Droplets,
    gradient: 'from-blue-600 to-blue-800', bg: 'bg-blue-50', accent: 'text-brand-blue',
    title: 'Sanitary Disposal Services', tagline: 'Hygienic. Discreet. Reliable.',
    image: 'https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?auto=format&fit=crop&w=900&q=72&fm=webp',
    desc: "Our sanitary disposal services provide hygienic, discreet and compliant collection and disposal of sanitary waste from ladies' washrooms, hospitals, schools and public facilities. We supply and maintain purpose-built sanitary bins with regular scheduled collection.",
    features: ['Sanitary bin supply, installation & maintenance','Weekly or bi-weekly scheduled collection','Medical-grade incineration and disposal','Compliance with Environmental Management Act','Fresh-scented bin liners with every collection','Staff training on disposal best practices','Provision for all facility sizes','Same-day emergency bin replacement'],
    benefits: ['NEMA-compliant disposal certificates provided','Complete hygiene and odour control','Flexible service contracts','Discreet, uniformed collection team'],
  },
  {
    id: 'industrial-waste', icon: Factory,
    gradient: 'from-orange-600 to-orange-800', bg: 'bg-orange-50', accent: 'text-orange-600',
    title: 'Hazards & Industrial Waste Management', tagline: 'Safe handling. Compliant disposal.',
    image: 'https://images.unsplash.com/photo-1665132495454-ab68feef4163?auto=format&fit=crop&w=900&q=72&fm=webp',
    desc: 'Industrial and hazardous waste demands expert handling. Lidav is fully licensed to handle, transport and dispose of chemical waste, clinical waste, electronic waste and other hazardous materials in strict compliance with NEMA and KEBS regulations.',
    features: ['Chemical and laboratory waste disposal','Clinical and medical waste management','Electronic waste (e-waste) recycling','Construction and demolition debris removal','Asbestos assessment and safe removal','Oil and solvent waste collection','Manifested waste transfer and documentation','Emergency hazmat spill response'],
    benefits: ['Full NEMA compliance documentation','Licensed hazardous waste transporters','Chain of custody records for all waste','Staff PPE and safety training included'],
  },
  {
    id: 'general-waste', icon: Recycle,
    gradient: 'from-red-600 to-red-800', bg: 'bg-red-50', accent: 'text-brand-red',
    title: 'General Waste Management', tagline: 'Clean spaces. Responsible recycling.',
    image: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?auto=format&fit=crop&w=900&q=72&fm=webp',
    desc: 'From daily bin collection to large-scale commercial waste contracts, Lidav offers comprehensive general waste management services. We promote sustainability through sorting, recycling and responsible disposal, helping our clients reduce their environmental footprint.',
    features: ['Daily, weekly or scheduled bin collection','Sorting and segregation at source','Cardboard, paper and plastic recycling','Organic waste composting programs','Skip hire for construction and events','Compactors and baling equipment provision','Waste audits and reduction consultation','Certificates of waste disposal issued'],
    benefits: ['Reduced landfill costs through recycling','ESG reporting data provided','Flexible contract terms','Dedicated account manager'],
  },
  {
    id: 'landscaping', icon: TreePine,
    gradient: 'from-red-500 to-red-700', bg: 'bg-red-50', accent: 'text-brand-red',
    title: 'Landscaping Services', tagline: 'Lush gardens. Stunning spaces.',
    image: '/images/landscaping.png',
    desc: "Transform your outdoor spaces with Lidav's expert landscaping services. From garden design and installation to ongoing lawn maintenance and tree management, our horticultural team creates and maintains beautiful, sustainable green spaces that enhance your property's value and appeal.",
    features: ['Garden design and landscape architecture','Lawn installation (turf laying and seeding)','Regular lawn mowing and edging','Tree and shrub planting and maintenance','Irrigation system installation','Flower bed design and replanting','Garden lighting installation','Driveway and pathway hardscaping'],
    benefits: ['Certified horticulturists on every project','Drought-resistant and indigenous plant options','Seasonal planting schedules','Regular maintenance contracts available'],
  },
];

function ServiceCard({ service, onClick, isActive }) {
  const { icon: Icon, gradient, bg, title } = service;
  return (
    <button
      onClick={() => onClick(service.id)}
      className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-200 cursor-pointer group"
      style={isActive
        ? {
            background:     'linear-gradient(135deg, #02AEF2, #018bc2)',
            color:          '#111827',
            boxShadow:      '0 8px 32px rgba(2,174,242,0.30)',
            border:         '1px solid rgba(2,174,242,0.3)',
            transform:      'translateX(4px)',
          }
        : {
            background:     'rgba(255,255,255,0.82)',
            backdropFilter: 'blur(8px)',
            border:         '1px solid rgba(226,232,240,0.8)',
            color:          '#374151',
          }
      }
    >
      <div className={`w-10 h-10 ${isActive ? 'bg-black/10' : bg} rounded-xl flex items-center justify-center flex-shrink-0 transition-colors`}>
        <div className={`w-6 h-6 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center`}>
          <Icon size={13} className="text-white" />
        </div>
      </div>
      <span className="text-sm font-semibold font-poppins line-clamp-2">{title}</span>
    </button>
  );
}

export default function Services() {
  const [activeId, setActiveId] = useState(services[0].id);
  const active = services.find(s => s.id === activeId);

  const handleSelect = useCallback((id) => {
    setActiveId(id);
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        document.getElementById('service-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, []);

  return (
    <main>
      <PageSEO {...servicesSEO} />
      {/* ── Hero ── */}
      <section className="relative min-h-[45vh] flex items-center bg-brand-blue overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/1.jpg" alt="Services" className="w-full h-full object-cover opacity-10" fetchpriority="high" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/95 to-brand-blue/80" />
        <div className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        <div className="pointer-events-none absolute -top-20 right-0 w-80 h-80 bg-brand-red/6 rounded-full blur-3xl animate-blob" style={{ animationDelay: '1s' }} />
        <div className="pointer-events-none absolute bottom-0 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-blob" style={{ animationDelay: '3.5s' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div
              className="inline-flex items-center gap-2 text-gray-700 text-xs font-inter mb-5 px-3 py-1.5 rounded-full border"
              style={{
                background:           'rgba(0,0,0,0.07)',
                backdropFilter:       'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderColor:          'rgba(0,0,0,0.08)',
              }}
            >
              <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-gray-900 font-semibold">Services</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-poppins text-gray-900 mb-4">
              Our <span className="text-brand-red">Services</span>
            </h1>
            <p className="text-gray-700 text-lg font-inter max-w-2xl font-medium">
              Six comprehensive service lines designed to protect every aspect of your health, environment and space.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-14 bg-[#f0f7ff]" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }} />
      </section>

      {/* ── Services Explorer ── */}
      <section className="py-20 bg-[#f0f7ff] relative overflow-hidden">
        <div className="pointer-events-none absolute top-0 left-0 w-64 h-64 bg-brand-blue/4 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-0 w-64 h-64 bg-brand-red/3 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div {...fadeUp()} className="text-center mb-12">
            <span className="section-tag">Everything Under One Roof</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mt-2">
              Choose a Service to <span className="text-gradient-brand">Explore</span>
            </h2>
          </motion.div>

          {/* Mobile tab strip */}
          <div className="lg:hidden overflow-x-auto -mx-4 px-4 mb-6">
            <div className="flex gap-2 pb-2">
              {services.map(({ id, icon: Icon, gradient, bg, title }) => (
                <button
                  key={id}
                  onClick={() => handleSelect(id)}
                  className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold font-poppins transition-all duration-200 whitespace-nowrap cursor-pointer"
                  style={id === activeId
                    ? { background: 'linear-gradient(135deg, #02AEF2, #018bc2)', color: '#111827', boxShadow: '0 8px 24px rgba(2,174,242,0.25)', border: '1px solid transparent' }
                    : { background: 'rgba(255,255,255,0.88)', color: '#4B5563', border: '1px solid rgba(226,232,240,0.8)' }
                  }
                >
                  <div className={`w-7 h-7 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <div className={`w-5 h-5 bg-gradient-to-br ${gradient} rounded-md flex items-center justify-center`}>
                      <Icon size={11} className="text-white" />
                    </div>
                  </div>
                  {title}
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Desktop Sidebar */}
            <div className="hidden lg:block lg:col-span-4 xl:col-span-3">
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="space-y-2 sticky top-28"
              >
                {services.map(s => (
                  <motion.div key={s.id} variants={staggerItem}>
                    <ServiceCard service={s} onClick={handleSelect} isActive={s.id === activeId} />
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Detail Panel */}
            <div id="service-detail" className="lg:col-span-8 xl:col-span-9 scroll-mt-6">
              <motion.div
                key={activeId}
                initial={{ opacity: 0, x: 20, scale: 0.98 }}
                animate={{ opacity: 1, x: 0,  scale: 1    }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="rounded-3xl overflow-hidden"
                style={{
                  background:           'rgba(255,255,255,0.90)',
                  backdropFilter:       'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border:               '1px solid rgba(255,255,255,0.70)',
                  boxShadow:            '0 8px 40px rgba(2,174,242,0.10)',
                }}
              >
                {/* Hero image */}
                <div className="relative h-56 sm:h-72 md:h-96 lg:h-[420px] overflow-hidden bg-gray-100">
                  <img
                    src={active.image}
                    alt={active.title}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: active.id === 'landscaping' ? 'center 42%' : 'center' }}
                  />
                  {active.id === 'general-waste' && (
                    <div
                      className="absolute top-6 right-6 p-3 rounded-2xl animate-float"
                      style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid rgba(255,255,255,0.8)' }}
                    >
                      <img src="/images/logo.jpg" alt="Lidav Logo" className="h-10 w-auto" loading="lazy" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <div
                      className={`inline-flex items-center gap-2 ${active.bg} px-3 py-1.5 rounded-full mb-3`}
                      style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                    >
                      <div className={`w-6 h-6 bg-gradient-to-br ${active.gradient} rounded-lg flex items-center justify-center`}>
                        <active.icon size={12} className="text-white" />
                      </div>
                      <span className={`text-xs font-semibold font-poppins ${active.accent}`}>Lidav Service</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold font-poppins text-white">{active.title}</h2>
                    <p className="text-white/80 text-sm font-inter mt-1 font-medium">{active.tagline}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-8">
                  <p className="text-gray-600 font-inter leading-relaxed mb-8">{active.desc}</p>

                  <div className="grid sm:grid-cols-2 gap-8">
                    {/* Features */}
                    <div>
                      <h3 className="font-bold font-poppins text-gray-900 mb-4 flex items-center gap-2">
                        <div className={`w-6 h-0.5 bg-gradient-to-r ${active.gradient} rounded-full`} />
                        What's Included
                      </h3>
                      <ul className="space-y-2.5">
                        {active.features.map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600 font-inter">
                            <CheckCircle2 size={15} className="text-brand-red flex-shrink-0 mt-0.5" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Benefits */}
                    <div>
                      <h3 className="font-bold font-poppins text-gray-900 mb-4 flex items-center gap-2">
                        <div className={`w-6 h-0.5 bg-gradient-to-r ${active.gradient} rounded-full`} />
                        Key Benefits
                      </h3>
                      <ul className="space-y-2.5">
                        {active.benefits.map((b) => (
                          <li key={b} className="flex items-start gap-2.5 text-sm text-gray-600 font-inter">
                            <div className="w-4 h-4 bg-brand-blue rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <div className="w-1.5 h-1.5 bg-white rounded-full" />
                            </div>
                            {b}
                          </li>
                        ))}
                      </ul>

                      <div
                        className="mt-8 p-5 rounded-2xl border"
                        style={{
                          background:           'rgba(240,247,255,0.8)',
                          backdropFilter:       'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          borderColor:          'rgba(2,174,242,0.12)',
                        }}
                      >
                        <p className="text-gray-700 font-inter text-sm mb-4 font-medium">
                          Ready to get started? Request a free quote today.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Link
                            to="/contact"
                            className={`flex items-center gap-2 bg-gradient-to-r ${active.gradient} text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:-translate-y-0.5 transition-all shadow-md cursor-pointer`}
                          >
                            Request Quote <ArrowRight size={14} />
                          </Link>
                          <a
                            href="tel:0722148999"
                            className="flex items-center gap-2 border border-gray-200 text-gray-700 hover:bg-gray-100 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer"
                          >
                            <Phone size={13} /> Call Now
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── All Services Quick Grid ── */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at center top, rgba(2,174,242,0.04) 0%, transparent 60%)' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div {...fadeUp()} className="text-center mb-12">
            <span className="section-tag">All Services At a Glance</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mt-2">
              Comprehensive Facilities <span className="text-gradient-brand">Management</span>
            </h2>
          </motion.div>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          >
            {services.map(({ icon: Icon, gradient, bg, accent, title, tagline, id }) => (
              <motion.div
                key={id}
                variants={staggerItem}
                onClick={() => {
                  handleSelect(id);
                  if (window.innerWidth >= 1024) window.scrollTo({ top: 400, behavior: 'smooth' });
                }}
                className="cursor-pointer rounded-2xl p-5 border transition-all duration-300 group hover:-translate-y-1"
                style={{
                  background:   'rgba(248,250,252,1)',
                  borderColor:  'rgba(226,232,240,0.8)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background   = 'rgba(255,255,255,1)';
                  e.currentTarget.style.borderColor  = 'rgba(2,174,242,0.18)';
                  e.currentTarget.style.boxShadow    = '0 8px 32px rgba(2,174,242,0.10)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background   = 'rgba(248,250,252,1)';
                  e.currentTarget.style.borderColor  = 'rgba(226,232,240,0.8)';
                  e.currentTarget.style.boxShadow    = 'none';
                }}
              >
                <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <div className={`w-8 h-8 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center`}>
                    <Icon size={15} className="text-white" />
                  </div>
                </div>
                <h3 className={`font-bold font-poppins text-sm text-gray-900 mb-1 group-hover:text-brand-blue transition-colors`}>{title}</h3>
                <p className="text-gray-400 text-xs font-inter">{tagline}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 bg-brand-blue relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 0)', backgroundSize: '28px 28px' }} />
        <div className="pointer-events-none absolute top-0 right-0 w-72 h-72 bg-brand-red/8 rounded-full blur-3xl animate-blob" style={{ animationDelay: '1s' }} />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <motion.div {...fadeUp()}>
            <h2 className="text-3xl font-bold font-poppins text-gray-900 mb-4">
              Not sure which service you need?
            </h2>
            <p className="text-gray-800 font-inter mb-8 font-medium">
              Our experts will assess your needs and recommend the right solution. Contact us for a free consultation.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                className="bg-brand-red hover:bg-brand-red-dark text-white px-8 py-3.5 rounded-full font-semibold font-poppins transition-all hover:-translate-y-0.5 shadow-lg flex items-center gap-2 cursor-pointer"
                style={{ boxShadow: '0 8px 32px rgba(227,6,19,0.25)' }}
              >
                Get Free Assessment <ArrowRight size={15} />
              </Link>
              <a
                href="tel:0722148999"
                className="border-2 border-black/12 text-gray-900 px-7 py-3.5 rounded-full font-semibold font-poppins transition-all hover:-translate-y-0.5 flex items-center gap-2 cursor-pointer"
                style={{ background: 'rgba(0,0,0,0.07)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
              >
                <Phone size={15} /> 0722 148 999
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
